import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    TextField,
    Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { getEffectiveDecayDays } from '../helper';

interface BlockUnblockModalProps {
    open: boolean;
    onClose: () => void;
    row: any;
    mode: 'block' | 'unblock';
    loading?: boolean;
    onConfirm?: (payload: {
        row: any;
        mode: 'block' | 'unblock';
        reasons: string[];
        note: string;
    }) => void;
}

const labelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    color: '#8A93B4',
    letterSpacing: 0.5,
    marginBottom: 4,
};
const valueStyle: React.CSSProperties = {
    fontSize: 18,
    fontWeight: 700,
    color: '#14112D',
};

const BLOCK_REASONS = [
    { key: 'WALLET', label: 'LOW OR NEGATIVE WALLET BALANCE' },
    { key: 'DECAY', label: 'INACTIVE (DECAY)' },
    { key: 'FRAUD', label: 'FRAUD REVIEW' },
    { key: 'MANUAL', label: 'MANUAL (OTHER)' },
];
const UNBLOCK_REASONS = [
    { key: 'WALLET_TOPPED_UP', label: 'WALLET TOPPED UP' },
    {
        key: 'DECAY_CLEARED',
        label: 'RIDER RESUMED ACTIVITY (DECAY CLEARED)',
    },
    { key: 'WARNED', label: 'WARNED RIDER (VERBAL/CALL)' },
    { key: 'FRAUD_CLEARED', label: 'FRAUD CLEARED' },
    { key: 'WRONG_BLOCK', label: 'WRONG BLOCK' },
    { key: 'OTHER', label: 'OTHER' },
];

const BlockUnblockModal: React.FC<BlockUnblockModalProps> = ({
    open,
    onClose,
    row,
    mode,
    loading = false,
    onConfirm,
}) => {
    const [selected, setSelected] = useState<Record<string, boolean>>({});
    const [note, setNote] = useState('');

    useEffect(() => {
        if (open) {
            setSelected({});
            setNote('');
        }
    }, [open, row]);

    if (!row) return null;

    const isBlock = mode === 'block';
    const reasonOptions = isBlock ? BLOCK_REASONS : UNBLOCK_REASONS;

    const rider = row?.taggedRiderDetails?.name ?? row?.riderName ?? '-';
    const evNo = row?.evVehicleNumber ?? row?.evNumber ?? '-';
    const wallet =
        row?.walletBalance ??
        row?.wallet ??
        row?.riderWalletBalance ??
        null;
    const decay =
        getEffectiveDecayDays(row) ??
        row?.inactiveDays ??
        row?.evProductivity?.usageDetails?.inactiveDays ??
        null;
    const moonlight =
        row?.moonlightPercentage ??
        row?.moonlightingPercentage ??
        row?.evProductivity?.moonlightingDetails?.moonlightingPercentage ??
        null;
    const bucket = (row?.bucket || '').toString().toUpperCase();
    const blockedForReason =
        row?.blockReason ?? row?.blockedForReason ?? 'Low or Negative Wallet Balance';

    const walletStr = wallet === null || wallet === undefined ? '-' : `₹${wallet}`;
    const decayStr =
        decay === null || decay === undefined ? '-' : `${decay}d`;
    const moonlightStr =
        moonlight === null || moonlight === undefined ? '-' : `${moonlight}%`;

    const selectedKeys = Object.keys(selected).filter((k) => selected[k]);
    const canConfirm = selectedKeys.length > 0;

    const firstName = (rider || '').split(' ')[0] || rider;
    const selectedReasonLabels = selectedKeys.length
        ? reasonOptions
              .filter((o) => selected[o.key])
              .map((o) => o.label)
              .join(', ')
        : '—';
    const blockPreview = `Hi ${firstName}, your LoadShare EV (${evNo}) has been disabled. Reason: ${selectedReasonLabels}. Please contact your TL.`;
    const unblockPreview = `Hi ${firstName}, your LoadShare EV (${evNo}) has been re-enabled. You can resume operations. For any issues, please contact your TL.`;
    const previewText = isBlock ? blockPreview : unblockPreview;

    const handleConfirm = () => {
        if (!canConfirm || loading) return;
        onConfirm?.({ row, mode, reasons: selectedKeys, note });
    };

    const toggle = (key: string) =>
        setSelected((prev) => ({ ...prev, [key]: !prev[key] }));

    return (
        <Dialog
            open={open}
            onClose={() => {
                if (!loading) onClose();
            }}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle style={{ paddingBottom: 8 }}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Typography
                        component="span"
                        style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: '#14112D',
                        }}
                    >
                        {isBlock ? 'Block' : 'Unblock'} {rider} · {evNo}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <Divider />
            <DialogContent style={{ paddingTop: 16 }}>
                <Box
                    p={2}
                    mb={2}
                    style={{
                        background: '#F6F7F9',
                        border: '1px solid #EBEDF8',
                        borderRadius: 8,
                    }}
                >
                    <Box
                        display="grid"
                        style={{
                            gridTemplateColumns: '1fr 1fr',
                            rowGap: 12,
                            columnGap: 12,
                        }}
                    >
                        <Box>
                            <Typography style={labelStyle}>
                                {isBlock ? 'WALLET' : 'WALLET (NOW)'}
                            </Typography>
                            <Typography
                                style={{
                                    ...valueStyle,
                                    color: isBlock ? '#f44336' : '#14112D',
                                }}
                            >
                                {walletStr}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography style={labelStyle}>
                                {isBlock ? 'DECAY' : 'DECAY (NOW)'}
                            </Typography>
                            <Typography
                                style={{
                                    ...valueStyle,
                                    color: isBlock ? '#f44336' : '#14112D',
                                }}
                            >
                                {decayStr}
                            </Typography>
                        </Box>
                        <Box style={{ gridColumn: 'span 2' }}>
                            <Typography style={labelStyle}>
                                {isBlock ? 'BUCKET' : 'BLOCKED FOR'}
                            </Typography>
                            <Typography style={valueStyle}>
                                {isBlock ? bucket || '-' : blockedForReason}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Typography style={labelStyle}>
                    {isBlock
                        ? 'REASON CODES (SELECT ALL THAT APPLY — AT LEAST ONE REQUIRED)'
                        : 'REASONS FOR UNBLOCK (SELECT ALL THAT APPLY — AT LEAST ONE REQUIRED)'}
                </Typography>
                <Box
                    p={1}
                    mb={2}
                    style={{
                        background: '#F6F7F9',
                        border: '1px solid #EBEDF8',
                        borderRadius: 8,
                    }}
                >
                    {reasonOptions.map((opt) => {
                        const checked = !!selected[opt.key];
                        return (
                            <Box
                                key={opt.key}
                                display="flex"
                                alignItems="center"
                                onClick={() => toggle(opt.key)}
                                style={{
                                    padding: '6px 8px',
                                    borderRadius: 6,
                                    background: checked ? '#EEEAFB' : 'transparent',
                                    cursor: 'pointer',
                                }}
                            >
                                <Checkbox
                                    checked={checked}
                                    onChange={() => toggle(opt.key)}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ padding: 4, color: '#43419e' }}
                                />
                                <Typography
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: '#515f6d',
                                        letterSpacing: 0.5,
                                        marginLeft: 4,
                                    }}
                                >
                                    {opt.label}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                <Typography style={labelStyle}>
                    NOTE TO AUDIT LOG (OPTIONAL)
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    placeholder={
                        isBlock
                            ? 'Context for other ops / appeal review...'
                            : 'Context for the exception — what changed?'
                    }
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    style={{ marginBottom: 16 }}
                />

                <Typography style={labelStyle}>
                    RIDER WILL RECEIVE (WHATSAPP + PUSH)
                </Typography>
                <Box
                    p={2}
                    mb={2}
                    style={{
                        background: '#F0FDF4',
                        border: '1px solid #BBF7D0',
                        borderRadius: 8,
                        fontSize: 14,
                        color: '#14532D',
                        lineHeight: 1.6,
                    }}
                >
                    <Typography
                        style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: '#15803D',
                            letterSpacing: 0.5,
                            marginBottom: 6,
                        }}
                    >
                        PREVIEW
                    </Typography>
                    {previewText}
                </Box>

                <Divider style={{ marginBottom: 16 }} />

                <Box
                    display="flex"
                    justifyContent="flex-end"
                    style={{ gap: 8 }}
                >
                    <Button
                        onClick={onClose}
                        disabled={loading}
                        variant="outlined"
                        style={{
                            borderRadius: 8,
                            textTransform: 'none',
                            fontWeight: 700,
                            padding: '8px 20px',
                            borderColor: '#EBEDF8',
                            color: '#14112D',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={!canConfirm || loading}
                        variant="contained"
                        startIcon={
                            loading ? (
                                <CircularProgress size={16} style={{ color: '#fff' }} />
                            ) : undefined
                        }
                        style={{
                            borderRadius: 8,
                            textTransform: 'none',
                            fontWeight: 700,
                            padding: '8px 20px',
                            background: canConfirm
                                ? isBlock
                                    ? '#f44336'
                                    : '#43419e'
                                : '#EBEDF8',
                            color: canConfirm ? '#fff' : '#8A93B4',
                        }}
                    >
                        {loading
                            ? 'Processing…'
                            : isBlock
                            ? 'Confirm Block'
                            : 'Confirm Unblock'}
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default BlockUnblockModal;
